import React from "react";
import {
  Tabs,
  TabDataItem,
  RaportHSPSpecsData,
  RaportHSPSpecsProps,
} from "@/components";

/**
 * Funkcja pomocnicza do konwersji danych specyfikacji na format TabDataItem
 */
const convertSpecsToTabData = (
  data: RaportHSPSpecsData
): TabDataItem[] => {
  const tabData: TabDataItem[] = [];

  // Silnik i osiągi
  if (data.engine) {
    const labelMap: Record<string, string> = {
      engineType: "Typ silnika",
      engineDesignation: "Oznaczenie silnika",
      engineCapacity: "Pojemność silnika",
      power: "Moc",
      torque: "Moment obrotowy",
      fuelType: "Rodzaj paliwa",
      cylindersCount: "Liczba cylindrów",
    };

    Object.entries(data.engine).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Silnik i osiągi",
          label: labelMap[key] || key,
          icon: "handshake",
          value: value,
        });
      }
    });
  }

  // Układ napędowy
  if (data.driveTrain) {
    const labelMap: Record<string, string> = {
      driveType: "Typ napędu",
      gearboxType: "Rodzaj skrzyni biegów",
      gearsCount: "Liczba biegów",
    };

    Object.entries(data.driveTrain).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Układ napędowy",
          label: labelMap[key] || key,
          icon: "network-nodes",
          value: value,
        });
      }
    });
  }

  // Nadwozie
  if (data.body) {
    const labelMap: Record<string, string> = {
      bodyType: "Typ nadwozia",
      doorsCount: "Liczba drzwi",
      seatsCount: "Liczba miejsc",
      trunkCapacity: "Pojemność bagażnika",
      fuelTankCapacity: "Pojemność zbiornika paliwa",
    };

    Object.entries(data.body).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Nadwozie",
          icon: "car",
          label: labelMap[key] || key,
          value: value,
        });
      }
    });
  }

  // Wymiary i masa
  if (data.dimensions) {
    const labelMap: Record<string, string> = {
      length: "Długość",
      width: "Szerokość",
      height: "Wysokość",
      wheelbase: "Rozstaw osi",
      frontTrack: "Rozstaw kół przednich",
      rearTrack: "Rozstaw kół tylnych",
      groundClearance: "Prześwit",
      curbWeight: "Masa własna",
      maxWeight: "Masa całkowita",
      towingCapacity: "Maksymalna masa przyczepy",
    };

    Object.entries(data.dimensions).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Wymiary i masa",
          icon: "person-shirt",
          label: labelMap[key] || key,
          value: value,
        });
      }
    });
  }

  // Koła i zawieszenie
  if (data.wheelsSuspension) {
    const labelMap: Record<string, string> = {
      frontWheelSize: "Rozmiar felg przednich",
      rearWheelSize: "Rozmiar felg tylnych",
      frontTireSize: "Rozmiar opon przednich",
      rearTireSize: "Rozmiar opon tylnych",
      frontSuspension: "Zawieszenie przednie",
      rearSuspension: "Zawieszenie tylne",
      frontBrakes: "Hamulce przednie",
      rearBrakes: "Hamulce tylne",
      steeringType: "Typ kierownicy",
    };

    Object.entries(data.wheelsSuspension).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Koła i zawieszenie",
          icon: "network-connection",
          label: labelMap[key] || key,
          value: value,
        });
      }
    });
  }

  // Zużycie paliwa i emisje
  if (data.fuelEmission) {
    const labelMap: Record<string, string> = {
      fuelConsumptionCity: "Zużycie paliwa w mieście",
      fuelConsumptionHighway: "Zużycie paliwa poza miastem",
      fuelConsumptionCombined: "Zużycie paliwa łącznie",
      co2Emission: "Emisja CO2",
      emissionStandard: "Norma emisji spalin",
      fuelTankCapacity: "Pojemność zbiornika paliwa",
      range: "Zasięg",
    };

    Object.entries(data.fuelEmission).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Zużycie paliwa i emisje",
          icon: "gas",
          label: labelMap[key] || key,
          value: value,
        });
      }
    });
  }

  // Inne informacje
  if (data.other) {
    const labelMap: Record<string, string> = {
      airConditioning: "Klimatyzacja",
      heatingSystem: "System ogrzewania",
      soundSystem: "System audio",
      navigationSystem: "System nawigacji",
      keylessEntry: "Dostęp bezkluczykowy",
      startSystem: "System uruchamiania",
      warranty: "Gwarancja",
      serviceInterval: "Interwał serwisowy",
    };

    Object.entries(data.other).forEach(([key, value]) => {
      if (value) {
        tabData.push({
          title: "Inne informacje",
          icon: "i",
          label: labelMap[key] || key,
          value: value,
        });
      }
    });
  }

  return tabData;
};

export const HSPSpecs: React.FC<Pick<RaportHSPSpecsProps, "data">> = ({
  data,
}) => {
  const tabData = convertSpecsToTabData(data);

  return (
    <div className="mt-6 space-y-8 lg:mt-10">
      <Tabs
        data={tabData}
        defaultValue="silnik-i-osiągi"
        className="mt-6"
        contentSize="wide"
        prefix="dane-techniczne"
      />
    </div>
  );
};
